import { Fragment, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KeyRound, CheckCircle2, X, Inbox, GraduationCap, UserCircle as UserIcon, CalendarDays, Paperclip, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Leaves from "@/pages/Leaves";

export default function Requests({ embedded } = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [leaves, setLeaves] = useState({ pending: [], recent: [] });
  const [studentLeaves, setStudentLeaves] = useState({ pending: [], recent: [] });
  const [subTeachers, setSubTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pw");

  const load = async () => {
    try {
      const [pw, lv, sl, sub] = await Promise.all([
        api.get("/requests"),
        api.get("/leaves"),
        api.get("/student-leaves").catch(() => ({ data: { pending: [], recent: [] } })),
        api.get("/leaves/teachers").catch(() => ({ data: [] })),
      ]);
      setItems(pw.data.items || []);
      setLeaves(lv.data || { pending: [], recent: [] });
      setStudentLeaves(sl.data || { pending: [], recent: [] });
      setSubTeachers(sub.data || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decideLeave = async (id, action, slot_assignments) => {
    try {
      const url = `/leaves/${id}/${action}`;
      await api.post(url, action === "approve" ? { slot_assignments: slot_assignments || [] } : {});
      toast.success(action === "approve" ? "Leave approved" : "Leave rejected");
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };

  const decideStudentLeave = async (id, action) => {
    try {
      await api.post(`/student-leaves/${id}/${action}`);
      toast.success(action === "acknowledge" ? "Leave acknowledged — attendance stays marked 'on leave'." : "Leave rejected.");
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };

  const openStudentAttachment = async (id) => {
    try {
      const res = await api.get(`/student-leaves/${id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the leave letter."); }
  };

  const Wrap = embedded ? Fragment : Layout;
  return (
    <Wrap>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Inbox</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Requests</h1>
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        <TabBtn active={tab === "pw"} onClick={() => setTab("pw")} icon={KeyRound} label={`Password resets (${items.length})`} testid="tab-pw" />
        <TabBtn active={tab === "sleaves"} onClick={() => setTab("sleaves")} icon={GraduationCap} label={`Student leaves (${studentLeaves.pending.length})`} testid="tab-student-leaves" />
        {isAdmin && (
          <TabBtn active={tab === "leaves"} onClick={() => setTab("leaves")} icon={CalendarDays} label={`Staff leaves (${leaves.pending.length})`} testid="tab-leaves" />
        )}
        {!isAdmin && (
          <TabBtn active={tab === "apply-leave"} onClick={() => setTab("apply-leave")} icon={CalendarPlus} label="Apply Leave" testid="tab-apply-leave" />
        )}
      </div>

      {tab === "apply-leave" ? (
        <div data-testid="apply-leave-panel"><Leaves embedded /></div>
      ) : loading ? <div className="text-slate-500">Loading…</div> : tab === "pw" ? (
        items.length === 0 ? (
          <Empty msg="No pending password requests." testid="requests-empty" />
        ) : (
          <div className="space-y-3" data-testid="requests-list">{items.map((r) => <RequestCard key={r.id} r={r} onChange={load} />)}</div>
        )
      ) : tab === "sleaves" ? (
        studentLeaves.pending.length === 0 && studentLeaves.recent.length === 0 ? (
          <Empty msg="No student leave requests yet." testid="student-leaves-empty" />
        ) : (
          <div className="space-y-3" data-testid="student-leaves-list">
            {studentLeaves.pending.map((l) => (
              <StudentLeaveCard key={l.id} l={l} onDecide={decideStudentLeave} onOpenAttachment={openStudentAttachment} />
            ))}
            {studentLeaves.recent.map((l) => (
              <StudentLeaveCard key={l.id} l={l} readOnly onOpenAttachment={openStudentAttachment} />
            ))}
          </div>
        )
      ) : (
        leaves.pending.length === 0 && leaves.recent.length === 0 ? (
          <Empty msg="No leave requests yet." testid="leaves-empty" />
        ) : (
          <div className="space-y-3" data-testid="leaves-list">
            {leaves.pending.map((l) => <LeaveCard key={l.id} l={l} onDecide={decideLeave} />)}
            {leaves.recent.filter(r => r.status !== "pending").slice(0, 10).map((l) => <LeaveCard key={l.id} l={l} readOnly />)}
          </div>
        )
      )}
    </Wrap>
  );
}

function StudentLeaveCard({ l, onDecide, readOnly, onOpenAttachment }) {
  const tone = l.status === "reviewed" ? "emerald" : l.status === "rejected" ? "rose" : "amber";
  const span = l.from_date === l.to_date ? l.from_date : `${l.from_date} → ${l.to_date}`;
  const sessLabel = { morning: "Morning", afternoon: "Afternoon", all_day: "All day", full_day: "All day" }[l.session] || l.session;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`student-leave-${l.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center"><GraduationCap className="h-5 w-5"/></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {l.student_name}
            <span className="ml-2 text-xs text-slate-400">Roll {l.roll_number} · {l.class_name}-{l.section}</span>
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full bg-${tone}-50 text-${tone}-700`}>{l.status === "reviewed" ? "acknowledged" : l.status}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{span} · {sessLabel}</div>
          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{l.reason}</div>
          {l.has_attachment && (
            <button onClick={() => onOpenAttachment(l.id)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:underline" data-testid={`student-leave-attach-${l.id}`}>
              <Paperclip className="h-3.5 w-3.5" /> View leave letter
            </button>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button onClick={() => onDecide(l.id, "acknowledge")} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`student-leave-ack-${l.id}`}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
            </Button>
            <Button onClick={() => onDecide(l.id, "reject")} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`student-leave-reject-${l.id}`}><X className="h-4 w-4"/></Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function Empty({ msg, testid }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-10 text-center" data-testid={testid}>
      <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <div className="text-slate-700 dark:text-slate-200 font-medium">{msg}</div>
    </div>
  );
}

function LeaveCard({ l, onDecide, readOnly }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState(null);
  const [picks, setPicks] = useState({}); // slot_id -> teacher_id
  const [candByKey, setCandByKey] = useState({}); // "date|start|end" -> list
  const [loadingSlot, setLoadingSlot] = useState("");
  const tone = l.status === "approved" ? "emerald" : l.status === "rejected" ? "rose" : "amber";

  const loadSlots = async () => {
    try {
      const { data } = await api.get(`/leaves/${l.id}/slots`);
      setSlots(data.slots || []);
      // Pre-populate any saved assignments (re-opening an approved leave)
      const saved = {};
      (data.slots || []).forEach((s) => { if (s.replacement_teacher_id) saved[s.slot_id] = s.replacement_teacher_id; });
      setPicks(saved);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load slots.");
      setSlots([]);
    }
  };

  const expand = async () => {
    if (!open) { setOpen(true); if (slots === null) await loadSlots(); }
    else setOpen(false);
  };

  const candidatesFor = async (slot) => {
    const key = `${slot.date}|${slot.start_time}|${slot.end_time}`;
    if (candByKey[key]) return candByKey[key];
    setLoadingSlot(key);
    try {
      const { data } = await api.get(`/leaves/${l.id}/slot-candidates`, {
        params: { date: slot.date, start_time: slot.start_time, end_time: slot.end_time },
      });
      setCandByKey((m) => ({ ...m, [key]: data }));
      return data;
    } catch { return []; }
    finally { setLoadingSlot(""); }
  };

  const allCovered = slots && slots.length > 0 && slots.every((s) => picks[s.slot_id]);

  const approve = () => {
    const assignments = (slots || []).map((s) => ({
      slot_id: s.slot_id,
      replacement_teacher_id: picks[s.slot_id] || null,
    }));
    onDecide(l.id, "approve", assignments);
  };

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`leave-${l.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center"><CalendarDays className="h-5 w-5"/></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{l.teacher_name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full bg-${tone}-50 text-${tone}-700`}>{l.status}</span></div>
          <div className="text-xs text-slate-500 mt-0.5">{l.start_date} → {l.end_date} · {l.days} day(s)</div>
          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{l.reason}</div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button onClick={expand} variant="outline" data-testid={`leave-toggle-slots-${l.id}`}>
              {open ? "Hide slots" : `Assign substitutes`}
            </Button>
            <Button onClick={() => onDecide(l.id, "reject")} variant="outline" className="text-rose-600 border-rose-200" data-testid={`leave-reject-${l.id}`}><X className="h-4 w-4"/></Button>
          </div>
        )}
      </div>

      {!readOnly && open && (
        <div className="mt-4 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden" data-testid={`leave-slots-${l.id}`}>
          {slots === null ? (
            <div className="p-4 text-sm text-slate-500">Loading slots…</div>
          ) : slots.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No timetable slots found for this teacher during the leave period.</div>
          ) : (
            <SlotsByDate
              slots={slots}
              picks={picks}
              setPicks={setPicks}
              candidatesFor={candidatesFor}
              loadingKey={loadingSlot}
            />
          )}
          {slots && slots.length > 0 && (
            <div className="flex items-center justify-between gap-2 p-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30">
              <div className="text-xs text-slate-500">
                {Object.keys(picks).filter((k) => picks[k]).length} of {slots.length} slot(s) covered
              </div>
              <Button
                onClick={approve}
                disabled={!allCovered}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid={`leave-approve-${l.id}`}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve {allCovered ? "" : "(cover all slots first)"}
              </Button>
            </div>
          )}
        </div>
      )}

      {readOnly && l.slot_assignments?.length > 0 && (
        <div className="mt-3 text-[11px] text-slate-500">
          {l.slot_assignments.filter((a) => a.replacement_teacher_id).length}/{l.slot_assignments.length} slots covered
        </div>
      )}
    </div>
  );
}

function SlotsByDate({ slots, picks, setPicks, candidatesFor, loadingKey }) {
  // Group slots by date, preserving order.
  const groups = [];
  const byDate = new Map();
  for (const s of slots) {
    if (!byDate.has(s.date)) { byDate.set(s.date, []); groups.push(s.date); }
    byDate.get(s.date).push(s);
  }
  const [open, setOpen] = useState(() => ({ [groups[0]]: true }));
  const fmt = (d) => {
    try {
      const obj = new Date(d);
      return obj.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return d; }
  };
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-700" data-testid="slots-by-date">
      {groups.map((d) => {
        const dayRows = byDate.get(d);
        const covered = dayRows.filter((s) => picks[s.slot_id]).length;
        const isOpen = !!open[d];
        return (
          <div key={d} data-testid={`slots-date-group-${d}`}>
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [d]: !o[d] }))}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-900/60 text-left"
              data-testid={`slots-date-toggle-${d}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{isOpen ? "▾" : "▸"}</span>
                <span className="font-medium text-sm">{fmt(d)}</span>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${covered === dayRows.length ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {covered}/{dayRows.length} covered
              </span>
            </button>
            {isOpen && (
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Class · Period</th>
                    <th className="px-3 py-2 text-left">Time</th>
                    <th className="px-3 py-2 text-left">Subject</th>
                    <th className="px-3 py-2 text-left">Substitute</th>
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map((s) => (
                    <SlotRow
                      key={s.slot_id}
                      s={s}
                      value={picks[s.slot_id] || ""}
                      onChange={(v) => setPicks((p) => ({ ...p, [s.slot_id]: v }))}
                      candidatesFor={candidatesFor}
                      loadingKey={loadingKey}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SlotRow({ s, value, onChange, candidatesFor, loadingKey }) {
  const [opts, setOpts] = useState(null);
  const key = `${s.date}|${s.start_time}|${s.end_time}`;
  const open = async () => {
    if (opts === null) {
      const list = await candidatesFor(s);
      setOpts(list);
    }
  };
  return (
    <tr className="border-t border-slate-100 dark:border-slate-700" data-testid={`leave-slot-${s.slot_id}`}>
      <td className="px-3 py-2 whitespace-nowrap">{s.class_name}-{s.section} · P{s.period}</td>
      <td className="px-3 py-2 whitespace-nowrap">{s.start_time}–{s.end_time}</td>
      <td className="px-3 py-2">{s.subject}</td>
      <td className="px-3 py-2">
        <Select value={value} onValueChange={onChange} onOpenChange={(o) => o && open()}>
          <SelectTrigger className="h-9 w-56" data-testid={`leave-slot-sub-${s.slot_id}`}>
            <SelectValue placeholder={loadingKey === key ? "Loading…" : "Pick a substitute"} />
          </SelectTrigger>
          <SelectContent>
            {(opts || []).map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`leave-slot-opt-${s.slot_id}-${t.id}`}>
                {t.name}{t.subject ? ` · ${t.subject}` : ""}
              </SelectItem>
            ))}
            {opts && opts.length === 0 && (
              <SelectItem value="__none__" disabled>No teacher free at this time</SelectItem>
            )}
          </SelectContent>
        </Select>
      </td>
    </tr>
  );
}

function RequestCard({ r, onChange }) {
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = r.requester_meta || {};

  const resolve = async () => {
    if (r.kind !== "student" && newPw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/requests/${r.id}/resolve`, r.kind === "student" ? {} : { new_password: newPw });
      toast.success(
        r.kind === "student"
          ? "Reset done · the student's password is now their Student ID. They'll set a new one on next login."
          : "Password reset · share the new password with the requester."
      );
      await onChange();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm("Reject this request?")) return;
    setBusy(true);
    try {
      await api.post(`/requests/${r.id}/reject`);
      toast.success("Request rejected.");
      await onChange();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const Icon = r.kind === "student" ? GraduationCap : UserIcon;
  const tone = r.kind === "student" ? "sky" : "violet";
  const toneCls = {
    sky: "border-sky-200 dark:border-sky-800 bg-white dark:bg-slate-800",
    violet: "border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-800",
  }[tone];

  return (
    <div className={`rounded-2xl border ${toneCls} p-5`} data-testid={`request-${r.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${r.kind === "student" ? "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300" : "bg-violet-50 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {r.requester_name}
            <span className="ml-2 text-xs uppercase tracking-[0.15em] text-slate-400 font-medium">{r.kind}</span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {r.kind === "student"
              ? <>Roll {meta.roll_number} · Class {meta.class_name}-{meta.section}</>
              : <>Identifier: <span className="font-mono">{meta.identifier || r.requester_identifier}</span></>}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Sent {new Date(r.created_at).toLocaleString()}</div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {r.kind !== "student" && (
            <Input
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="Set new password"
              className="h-10 w-56"
              data-testid={`request-pw-input-${r.id}`}
            />
          )}
          <Button onClick={resolve} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`request-resolve-${r.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> {r.kind === "student" ? "Reset to Student ID" : "Reset"}
          </Button>
          <Button onClick={reject} variant="outline" disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`request-reject-${r.id}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3 text-[11px] inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
        <KeyRound className="h-3 w-3" /> {r.kind === "student"
          ? "Resets the student's password to their Student ID and forces a change on next login."
          : "Setting a new password will require the teacher to use it on next login."}
      </div>
    </div>
  );
}
